import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Stack,
    Divider,
    Switch,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Visibility as VisibilityIcon,
    RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common/PageHeader';
import { templateService } from '../../services/templateService';
import { Template, TemplateCreate, VerbosityLevel } from '../../types/template';

export const TemplateManagementPage = () => {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Prefill setting
    const [prefillEnabled, setPrefillEnabled] = useState(() => {
        const saved = localStorage.getItem('template_prefill_enabled');
        return saved !== null ? saved === 'true' : true;
    });

    // Editor dialog state
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
    const [formData, setFormData] = useState<TemplateCreate>({
        name: '',
        description: '',
        content: '',
        verbosity: 'MEDIUM',
        instructions: '',
        is_default: false,
    });

    // Preview dialog
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

    // Delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);

    // Reset confirmation dialog
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [templateToReset, setTemplateToReset] = useState<Template | null>(null);

    // Load templates
    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await templateService.getAllTemplates();
            setTemplates(data);
        } catch (err: any) {
            setError(err.response?.data?.detail || t('errors.loadingError'));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEditor = (template?: Template) => {
        if (template) {
            setEditingTemplate(template);
            setFormData({
                name: template.name,
                description: template.description || '',
                content: template.content,
                verbosity: template.verbosity || 'MEDIUM',
                instructions: template.instructions || '',
                is_default: template.is_default,
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                name: '',
                description: '',
                content: '═══ SECTION 1 ═══\n- \n\n═══ SECTION 2 ═══\n- \n',
                verbosity: 'MEDIUM',
                instructions: '',
                is_default: false,
            });
        }
        setEditorOpen(true);
    };

    const handleCloseEditor = () => {
        setEditorOpen(false);
        setEditingTemplate(null);
    };

    const handleSaveTemplate = async () => {
        try {
            setError(null);
            if (editingTemplate) {
                await templateService.updateTemplate(editingTemplate.id, formData);
                setSuccess(t('templates.templateSaved'));
            } else {
                await templateService.createTemplate(formData);
                setSuccess(t('templates.templateSaved'));
            }
            handleCloseEditor();
            loadTemplates();
        } catch (err: any) {
            setError(err.response?.data?.detail || t('errors.savingError'));
        }
    };

    const handleSetDefault = async (template: Template) => {
        try {
            setError(null);
            await templateService.updateTemplate(template.id, { is_default: true });
            setSuccess(t('templates.defaultTemplateSet'));
            loadTemplates();
        } catch (err: any) {
            setError(err.response?.data?.detail || t('errors.savingError'));
        }
    };

    const handleOpenPreview = (template: Template) => {
        setPreviewTemplate(template);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setPreviewTemplate(null);
    };

    const handleOpenDeleteDialog = (template: Template) => {
        setTemplateToDelete(template);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    };

    const handleDeleteTemplate = async () => {
        if (!templateToDelete) return;

        try {
            setError(null);
            await templateService.deleteTemplate(templateToDelete.id);
            setSuccess(t('templates.templateDeleted'));
            handleCloseDeleteDialog();
            loadTemplates();
        } catch (err: any) {
            setError(err.response?.data?.detail || t('errors.deleteError'));
            handleCloseDeleteDialog();
        }
    };

    const handleOpenResetDialog = (template: Template) => {
        setTemplateToReset(template);
        setResetDialogOpen(true);
    };

    const handleCloseResetDialog = () => {
        setResetDialogOpen(false);
        setTemplateToReset(null);
    };

    const handleResetTemplate = async () => {
        if (!templateToReset) return;

        try {
            setError(null);
            await templateService.resetTemplate(templateToReset.id);
            setSuccess(t('templates.templateReset'));
            handleCloseResetDialog();
            loadTemplates();
        } catch (err: any) {
            setError(err.response?.data?.detail || t('errors.savingError'));
            handleCloseResetDialog();
        }
    };

    const handleTogglePrefill = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setPrefillEnabled(newValue);
        localStorage.setItem('template_prefill_enabled', String(newValue));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title={t('templates.title')}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Prefill Setting */}
            <Card sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('templates.autoPrefill')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('templates.autoPrefillDescription')}
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={prefillEnabled}
                                onChange={handleTogglePrefill}
                                color="primary"
                            />
                        }
                        label={prefillEnabled ? t('templates.enabled') : t('templates.disabled')}
                    />
                </Box>
            </Card>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenEditor()}
                >
                    {t('templates.createTemplate')}
                </Button>
            </Box>

            <Grid container spacing={3}>
                {templates.map((template) => (
                    <Grid item xs={12} md={6} lg={4} key={template.id}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                border: template.is_default ? 2 : 1,
                                borderColor: template.is_default ? 'primary.main' : 'divider',
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Stack spacing={1.5}>
                                    {/* Header */}
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography variant="h6" fontWeight={600}>
                                            {template.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleSetDefault(template)}
                                            disabled={template.is_default}
                                            color={template.is_default ? 'primary' : 'default'}
                                            title={template.is_default ? t('templates.defaultTemplate') : t('templates.setAsDefault')}
                                        >
                                            {template.is_default ? <StarIcon /> : <StarBorderIcon />}
                                        </IconButton>
                                    </Box>

                                    {/* Badges */}
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {template.is_default && (
                                            <Chip
                                                label={t('templates.defaultTemplate')}
                                                size="small"
                                                color="primary"
                                            />
                                        )}
                                        <Chip
                                            label={template.is_system ? t('templates.systemTemplate') : t('templates.customTemplate')}
                                            size="small"
                                            color={template.is_system ? 'default' : 'secondary'}
                                        />
                                        <Chip
                                            label={t(`templates.verbosity${template.verbosity.charAt(0).toUpperCase() + template.verbosity.slice(1)}`)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>

                                    {/* Description */}
                                    {template.description && (
                                        <Typography variant="body2" color="text.secondary">
                                            {template.description}
                                        </Typography>
                                    )}

                                    <Divider />

                                    {/* Content Preview */}
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                                            {t('templates.preview')}:
                                        </Typography>
                                        <Box
                                            sx={{
                                                bgcolor: 'grey.50',
                                                p: 1.5,
                                                borderRadius: 1,
                                                maxHeight: 200,
                                                overflow: 'auto',
                                                fontFamily: 'monospace',
                                                fontSize: '0.75rem',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {template.content.substring(0, 300)}
                                            {template.content.length > 300 && '...'}
                                        </Box>
                                    </Box>
                                </Stack>
                            </CardContent>

                            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                <Button
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleOpenPreview(template)}
                                >
                                    {t('templates.viewFull')}
                                </Button>
                                <Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenEditor(template)}
                                        title={t('common.edit')}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    {template.is_system ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenResetDialog(template)}
                                            color="warning"
                                            title={t('templates.resetTemplate')}
                                        >
                                            <ResetIcon fontSize="small" />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDeleteDialog(template)}
                                            color="error"
                                            title={t('common.delete')}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Editor Dialog */}
            <Dialog open={editorOpen} onClose={handleCloseEditor} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingTemplate ? t('templates.editTemplate') : t('templates.createTemplate')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label={t('templates.name')}
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            fullWidth
                        />
                        <TextField
                            label={t('templates.description')}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                        />
                        <TextField
                            label={t('templates.content')}
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            required
                            fullWidth
                            multiline
                            rows={12}
                            sx={{ fontFamily: 'monospace' }}
                        />
                        <TextField
                            label={t('templates.instructions')}
                            value={formData.instructions || ''}
                            onChange={(e) => setFormData({ ...formData, instructions: e.target.value.slice(0, 600) })}
                            fullWidth
                            multiline
                            rows={3}
                            helperText={`${(formData.instructions || '').length}/600 — ${t('templates.instructionsHelper')}`}
                            placeholder={t('templates.instructionsPlaceholder')}
                        />
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t('templates.verbosity')}</FormLabel>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                {t('templates.verbosityDescription')}
                            </Typography>
                            <RadioGroup
                                value={formData.verbosity || 'MEDIUM'}
                                onChange={(e) => setFormData({ ...formData, verbosity: e.target.value as VerbosityLevel })}
                            >
                                <FormControlLabel
                                    value="FULL"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {t('templates.verbosityFull')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('templates.verbosityFullExample')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="MEDIUM"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {t('templates.verbosityMedium')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('templates.verbosityMediumExample')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="SHORT"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {t('templates.verbosityShort')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('templates.verbosityShortExample')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditor}>{t('common.cancel')}</Button>
                    <Button
                        onClick={handleSaveTemplate}
                        variant="contained"
                        disabled={!formData.name || !formData.content}
                    >
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
                <DialogTitle>
                    {previewTemplate?.name}
                </DialogTitle>
                <DialogContent>
                    {previewTemplate && (
                        <Box>
                            {previewTemplate.description && (
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    {previewTemplate.description}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    bgcolor: 'grey.50',
                                    p: 2,
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {previewTemplate.content}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>{t('common.close')}</Button>
                    {previewTemplate && (
                        <Button
                            onClick={() => {
                                handleClosePreview();
                                handleOpenEditor(previewTemplate);
                            }}
                            variant="contained"
                            startIcon={<EditIcon />}
                        >
                            {t('common.edit')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>{t('templates.deleteConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('templates.deleteConfirmMessage')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleDeleteTemplate} color="error" variant="contained">
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Confirmation Dialog */}
            <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog}>
                <DialogTitle>{t('templates.resetConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('templates.resetConfirmMessage', { name: templateToReset?.name })}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResetDialog}>{t('common.cancel')}</Button>
                    <Button onClick={handleResetTemplate} color="warning" variant="contained">
                        {t('templates.resetTemplate')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
